/**
 * Rich Text Editor Component
 * Provides text formatting capabilities including bullets, numbering, and alignment
 */

import { useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Enter your content here...',
  className = '',
  minHeight = '200px'
}: RichTextEditorProps) {
  
  // Configure the toolbar with formatting options
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['clean']
    ],
  }), []);

  const formats = useMemo(() => [
    'header',
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'align'
  ], []);

  return (
    <div className={`rich-text-editor-wrapper ${className}`}>
      <style>{`
        .rich-text-editor-wrapper {
          width: 100%;
          position: relative;
        }
        
        .rich-text-editor-wrapper .quill {
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 0.75rem;
          display: block !important;
          width: 100%;
        }
        
        .rich-text-editor-wrapper .ql-toolbar {
          border: none;
          border-bottom: 1px solid #d1d5db;
          border-radius: 0.75rem 0.75rem 0 0;
          background: #f3f4f6;
          padding: 8px;
          display: block !important;
        }
        
        .rich-text-editor-wrapper .ql-container {
          border: none;
          border-radius: 0 0 0.75rem 0.75rem;
          font-size: 14px;
          min-height: ${minHeight};
          font-family: inherit;
          display: block !important;
        }
        
        .rich-text-editor-wrapper .ql-editor {
          min-height: ${minHeight};
          color: #1f2937;
          padding: 12px 15px;
          line-height: 1.6;
        }
        
        .rich-text-editor-wrapper .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
          left: 15px;
          right: 15px;
        }
        
        .rich-text-editor-wrapper .ql-stroke {
          stroke: #374151;
        }
        
        .rich-text-editor-wrapper .ql-fill {
          fill: #374151;
        }
        
        .rich-text-editor-wrapper .ql-picker-label {
          color: #374151;
        }
        
        .rich-text-editor-wrapper .ql-toolbar button {
          width: 28px;
          height: 28px;
        }
        
        .rich-text-editor-wrapper .ql-toolbar button:hover,
        .rich-text-editor-wrapper .ql-toolbar button:focus,
        .rich-text-editor-wrapper .ql-toolbar button.ql-active {
          color: #3b82f6;
        }
        
        .rich-text-editor-wrapper .ql-toolbar button:hover .ql-stroke,
        .rich-text-editor-wrapper .ql-toolbar button:focus .ql-stroke,
        .rich-text-editor-wrapper .ql-toolbar button.ql-active .ql-stroke {
          stroke: #3b82f6;
        }
        
        .rich-text-editor-wrapper .ql-toolbar button:hover .ql-fill,
        .rich-text-editor-wrapper .ql-toolbar button:focus .ql-fill,
        .rich-text-editor-wrapper .ql-toolbar button.ql-active .ql-fill {
          fill: #3b82f6;
        }
        
        .rich-text-editor-wrapper .ql-picker-options {
          background: white;
          border: 1px solid #d1d5db;
          z-index: 1000;
        }
        
        .rich-text-editor-wrapper .ql-picker-item:hover {
          color: #3b82f6;
        }
        
        .rich-text-editor-wrapper .ql-snow .ql-picker.ql-expanded .ql-picker-label {
          border-color: #d1d5db;
        }
        
        .rich-text-editor-wrapper .ql-snow .ql-picker.ql-expanded .ql-picker-options {
          border-color: #d1d5db;
        }
      `}</style>
      
      <ReactQuill
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        style={{ width: '100%', display: 'block' }}
      />
    </div>
  );
}
